<script lang="ts" setup>

// ─── IMPORTS ────────────────────────────────────────────────────────────────
import { User } from './models/user'
import { ref, onMounted, computed, watch } from 'vue'

import { useUserStore } from './store'

import {
  validateEmail,
  validatePasswords,
  generatePassword
} from './helpers'

// ─── STORES ────────────────────────────────────────────────────────────────
const userStore = useUserStore()

// ─── REFS: UI Flags and Data ───────────────────────────────────────────────
const loading = ref(false)
const activeEditUser = ref(false)
const activeCreateNewUser = ref(false)
const loadingCreateUser = ref(false)
const loadingEditUser = ref(false)
const searchUsers = ref('')
const page = ref(1)
const itemsPerPage = 10
const showPassword = ref(false)
const formTouched = ref(false)
const formRef = ref()
const formValid = ref(false)
const headers = [
  { title: 'NOMBRE', key: 'name' },
  { title: 'EMAIL', key: 'email' },
  { title: 'ROL', key: 'role' },
  { title: 'ESTADO', key: 'status' },
  { title: 'ACCIONES', key: 'actions' },
]

// ─── REFS: Usuario ──────────────────────────────────────────────────────
const users = ref<User[]>([])
const user = ref<User>(new User())
const password = ref('')
const confirmPassword = ref('')
const generateRandomPassword = ref(false)
const sendEmailPassword = ref(true)

// ─── ALERTAS ───────────────────────────────────────────────────────────────
const activeAlert = ref(false)
const textAlert = ref('')
const colorAlert = ref('error')

// ─── VALIDACIONES ──────────────────────────────────────────────────────────
const emailError = ref('')
const passwordError = ref('')

// ─── OPCIONES DE ROL ────────────────────────────────────────────────────────
const roleOptions = [
  { name: 'Administrador', key: 'admin' },
  { name: 'Supervisor', key: 'supervisor' }
]


// ─── COMPUTED ──────────────────────────────────────────────────────────────
const pageCount = computed(() => Math.ceil(users.value.length / itemsPerPage))

const getStatusLabel = (role: string) => {
  switch (role) {
    case 'admin':
      return 'Administrador'
    case 'supervisor':
      return 'Supervisor'
    case 'inactive':
      return 'Inactivo'
    default:
      return 'Desconocido'
  }
}

const getStatusColor = (role: string) => {
  switch (role) {
    case 'admin':
      return 'green'
    case 'supervisor':
      return 'blue'
    case 'inactive':
      return 'red'
    default:
      return 'grey'
  }
}

// ─── FUNCIONES AUXILIARES ──────────────────────────────────────────────────

// watcher para el checkbox de generar contraseña
watch(generateRandomPassword, (newVal) => {
  if (newVal) {
    const pwd = generatePassword()
    password.value = pwd
    confirmPassword.value = pwd
  } else {
    password.value = ''
    confirmPassword.value = ''
  }
})

// watcher para auto-generar folderName
watch([() => user.value.firstName, () => user.value.lastName], ([firstName, lastName]) => {
  if (firstName && lastName && !user.value.folderName) {
    const folderName = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`
      .replace(/[^a-z0-9_]/gi, '') // eliminar caracteres especiales
      .replace(/\s+/g, '_') // reemplazar espacios con guiones bajos
    user.value.folderName = folderName
  }
})

const resetForm = () => {
  user.value = new User()
  password.value = ''
  confirmPassword.value = ''
  generateRandomPassword.value = false
  sendEmailPassword.value = true
  emailError.value = ''
  passwordError.value = ''
  formTouched.value = false
}

// ─── EVENTOS / BOTONES ─────────────────────────────────────────────────────


const getUsers = async () => {
  loading.value = true
  try {
    users.value = await userStore.getEmployees()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al cargar los colaboradores. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  } finally {
    loading.value = false
  }
}

const btnSetActiveCreateUser = () => {
  activeCreateNewUser.value = !activeCreateNewUser.value
  resetForm()
}

const btnSetActiveEditUser = async (selectedUser: User) => {
  user.value = new User(selectedUser)
  // Limpiar campos de contraseña en modo edición
  password.value = ''
  confirmPassword.value = ''
  generateRandomPassword.value = false
  passwordError.value = ''
  emailError.value = ''
  activeEditUser.value = true
}

const createUser = async () => {
  await userStore.postUser({
    email: user.value.email,
    password: password.value,
    name: user.value.name,
    user: user.value.toPlainObject(),
    sendEmailPassword: sendEmailPassword.value
  })
}

const validateForm = (): boolean => {
  let isValid = true

  // Validación de campos obligatorios
  if (!user.value.firstName || !user.value.lastName || !user.value.email || !user.value.role || !user.value.folderName) {
    activeAlert.value = true
    textAlert.value = 'Todos los campos obligatorios son requeridos'
    colorAlert.value = 'error'
    isValid = false
  }

  // Validación de email
  if (!user.value.email) {
    emailError.value = 'Email requerido'
    isValid = false
  } else if (!validateEmail(user.value.email)) {
    emailError.value = 'Email no válido'
    isValid = false
  }

  // Validación de contraseñas
  const pwdValidation = validatePasswords(password.value, confirmPassword.value, generateRandomPassword.value)
  if (pwdValidation) {
    passwordError.value = pwdValidation
    isValid = false
    activeAlert.value = true
    textAlert.value = pwdValidation
    colorAlert.value = 'error'
  }

  return isValid
}

const copyToClipboard = () => {
  if (navigator?.clipboard) {
    navigator.clipboard.writeText(password.value)
      .then(() => {
        activeAlert.value = true
        textAlert.value = 'Contraseña copiada al portapapeles'
        colorAlert.value = 'success'
      })
      .catch(() => {
        activeAlert.value = true
        textAlert.value = 'Error al copiar la contraseña'
        colorAlert.value = 'error'
      });
  } else {
    activeAlert.value = true
    textAlert.value = 'Función de copiar no disponible'
    colorAlert.value = 'error'
  }
}

const btnCreateUser = async () => {
  formTouched.value = true

  const valid = await formRef.value?.validate()
  if (!valid.valid || !validateForm()) return

  user.value.name = user.value.firstName + ' ' + user.value.lastName
  user.value.type = 'teams'
  user.value.active = true

  loadingCreateUser.value = true
  try {
    await createUser()
    activeAlert.value = true
    textAlert.value = 'Colaborador creado exitosamente'
    colorAlert.value = 'success'
    activeCreateNewUser.value = false
    resetForm()
    await getUsers()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al crear el colaborador. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  } finally {
    loadingCreateUser.value = false
  }
}

const btnUpdateUser = async () => {
  user.value.name = user.value.firstName + ' ' + user.value.lastName
  user.value.type = 'teams'
  user.value.active = true

  loadingEditUser.value = true
  try {
    await userStore.putUser(user.value)
    activeAlert.value = true
    textAlert.value = 'Colaborador actualizado exitosamente'
    colorAlert.value = 'success'
    activeEditUser.value = false
    await getUsers()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al actualizar el colaborador. Por favor, intente nuevamente.'
    colorAlert.value = 'error'
  } finally {
    loadingEditUser.value = false
  }
}

const btnSendPasswordResetLink = async (email: string) => {
  try {
    await userStore.postSendPasswordResetLink(email)
    activeAlert.value = true
    textAlert.value = 'Enlace de restablecimiento enviado correctamente'
    colorAlert.value = 'success'
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al enviar el enlace de restablecimiento'
    colorAlert.value = 'error'
  }
}

// ─── LIFECYCLE ─────────────────────────────────────────────────────────────

onMounted(async () => {
  await getUsers()
})
</script>

<template>
  <v-card elevation="0" class="bg-background pa-5 rounded-lg">

    <div v-if="!activeCreateNewUser && !activeEditUser">

      <v-card class="bg-background mt-3 rounded-lg " elevation="3">
        <div class="rounded-lg justify-space-between mb-4 pa-2 d-flex">
          <v-card-title color="primary" class="text-subtitle-4">Colaboradores </v-card-title>
          <v-spacer />
          <v-btn v-if="!activeCreateNewUser && !activeEditUser" @click="btnSetActiveCreateUser" variant="text" text="Nuevo
            colaborador " />
          <v-btn v-else @click="(activeCreateNewUser = false), (activeEditUser = false)" variant="text"
            text="Volver a los colaboradores" />
        </div>
        <v-text-field v-model="searchUsers" append-inner-icon="mdi-magnify" density="compact" label="Buscar"
          variant="solo" hide-details single-line color="surface-light" class="mx-4" bg-color="background" />
        <v-data-table class="bg-background rounded-lg text-body-2" v-model:page="page" :headers="headers"
          :items="users" :search="searchUsers" :loading="loading" :items-per-page="itemsPerPage">
          <template v-slot:loading>
            <v-skeleton-loader class="bg-background " type="table-row@10" />
          </template>
          <template #[`item.name`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="36" class="mr-3" color="primary">
                <span class="text-white text-subtitle-2 font-weight-bold">
                  {{ item.name?.substring(0, 2).toUpperCase() }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption text-medium-emphasis">{{ item.folderName }}</div>
              </div>
            </div>
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip :color="getStatusColor(item.role)" class="text-caption" size="small" variant="tonal">
              {{ getStatusLabel(item.role) }}
            </v-chip>
          </template>
          <template #[`item.actions`]="{ item }">
            <div class="d-flex gap-1">
              <v-btn 
                variant="text" 
                @click="btnSetActiveEditUser(item)" 
                icon="mdi-pencil-outline"
                v-tooltip="`Editar`"
              ></v-btn>
              <v-btn 
                variant="text" 
                @click="btnSendPasswordResetLink(item.email)" 
                icon="mdi-lock-reset"
                v-tooltip="`Restablecer contraseña`"
                color="orange"
              ></v-btn>
            </div>
          </template>
          <template v-slot:bottom>
            <div class="text-center pt-2">
              <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
    <v-row v-if="activeCreateNewUser || activeEditUser" justify="center">
      <v-col cols="12" md="10" lg="8">
        <div>
          <v-card elevation="4" class="bg-background pa-0 ma-0 rounded-lg content-container">
            <!-- Sticky Action Bar  -->

            <div class="sticky-header bg-grey-lighten-4
 d-flex justify-space-between align-center px-4 py-3 " style="border-bottom: 1px solid #e0e0e0;">
              <span class="text-h6 font-weight-medium">{{ activeEditUser ? 'Editar colaborador' : 'Crear nuevo colaborador' }}</span>
              <div>
                <v-btn color="red" variant="tonal" class="mr-2" rounded="lg" @click="resetForm">Limpiar</v-btn>
                <v-btn color="primary" variant="tonal" class="mr-2" rounded="lg"
                  @click="activeCreateNewUser = false, activeEditUser = false">Volver</v-btn>
                <v-btn 
                  v-if="activeCreateNewUser" 
                  color="primary" 
                  class="text-white" 
                  rounded="lg" 
                  @click="btnCreateUser"
                  :loading="loadingCreateUser"
                >
                  Crear
                </v-btn>
                <v-btn 
                  v-if="activeEditUser" 
                  color="primary" 
                  class="text-white" 
                  rounded="lg" 
                  @click="btnUpdateUser"
                  :loading="loadingEditUser"
                >
                  Actualizar
                </v-btn>
              </div>
            </div>

            <!-- Main Content -->

            <v-row justify="center">
              <v-col cols="12">
                <v-sheet color="white" class="pa-6 " elevation="0">
                  <v-form ref="formRef" v-model="formValid" validate-on="submit lazy">

                    <!-- Main Content (dentro del mismo v-sheet) -->
                    <div class="pa-6">
                      <h4 class="text-h6 mb-6">1. Información del colaborador</h4>

                      <v-row>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="user.firstName" label="Nombre"
                            :error-messages="formTouched && !user.firstName ? ['Requerido'] : []" variant="outlined"
                            density="comfortable" bg-color="white" />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="user.lastName" label="Apellido"
                            :error-messages="formTouched && !user.lastName ? 'Requerido' : ''" variant="outlined"
                            density="comfortable" bg-color="white" />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="user.email" label="Email"
                            :error-messages="formTouched && emailError ? [emailError] : []"
                            @blur="emailError = !user.email ? 'Email requerido' : (!validateEmail(user.email) ? 'Email no válido' : '')"
                            variant="outlined" density="comfortable" bg-color="white" />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-select
                            v-model="user.role"
                            label="Rol *"
                            :items="roleOptions"
                            item-title="name"
                            item-value="key"
                            :error-messages="formTouched && !user.role ? ['Rol requerido'] : []"
                            variant="outlined"
                            density="comfortable"
                            bg-color="white"
                            hint="Seleccione el rol del colaborador"
                            persistent-hint
                          >
                          </v-select>
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="password" label="Contraseña" :type="showPassword ? 'text' : 'password'"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showPassword = !showPassword"
                            :error-messages="formTouched && passwordError ? passwordError : ''" variant="outlined"
                            density="comfortable" bg-color="white" 
                            :readonly="generateRandomPassword" />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field v-model="confirmPassword" label="Repetir contraseña"
                            :type="showPassword ? 'text' : 'password'"
                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showPassword = !showPassword"
                            :error-messages="formTouched && passwordError ? passwordError : ''" variant="outlined"
                            density="comfortable" bg-color="white" 
                            :readonly="generateRandomPassword" />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-checkbox label="Generar contraseña aleatoria" v-model="generateRandomPassword" hide-details
                            class="mt-2 mb-4"></v-checkbox>
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3" v-if="generateRandomPassword && password">
                          <v-btn 
                            variant="outlined" 
                            @click="copyToClipboard" 
                            prepend-icon="mdi-content-copy"
                            class="mt-2"
                          >
                            Copiar contraseña
                          </v-btn>
                        </v-col>

                        <v-col cols="12" md="6" class="mb-3">
                          <v-text-field 
                            v-model="user.folderName" 
                            label="Nombre de carpeta *"
                            :error-messages="formTouched && !user.folderName ? ['Nombre de carpeta requerido'] : []" 
                            persistent-hint
                          />
                        </v-col>
                        <v-col cols="12" md="6" class="mb-3">
                          <v-checkbox 
                            v-model="sendEmailPassword"
                            label="Enviar contraseña por email"
                            class="mt-2"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                    </div>
                  </v-form>
                  <!-- Remaining form sections would go here -->
                </v-sheet>
              </v-col>
            </v-row>


          </v-card>
        </div>
      </v-col>
    </v-row>
  </v-card>
  <v-snackbar v-model="activeAlert" :timeout="2000" :color="colorAlert">
    {{ textAlert }}

    <template v-slot:actions>
      <v-btn variant="text" @click="activeAlert = false">
        Cerrar
      </v-btn>
    </template>
  </v-snackbar>
</template>
<style scoped>
::-webkit-scrollbar {
  display: none;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 100;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.content-container {
  max-height: calc(100vh - 150px);
  /* o lo que se ajuste a tu layout */
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
</style>